import { Questionnaire } from '@aspendental/needs-based-questions';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'urql';
import { filterQuestionsQuery, needsBasedQuestionsQuery } from './NeedsBasedQuestions.helpers';
import OnixCard from './OnixCard';
import QuestionComponent from './QuestionComponent';
import ScriptingContainer from './ScriptingContainer';
import { REASON_FOR_VISIT_ID, REASONS_FOR_VISIT } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { useAppointmentStore, useReasonForVisitStore } from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
type Answers = Record<string, string[]>;
type QuestionShape = {
  questionId: string;
  answer?: {
    __typename?: string;
  };
};
function arrayEqual(a?: string[], b?: string[]) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
function answersEqual(a?: Answers, b?: Answers) {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (!(k in b)) return false;
    if (!arrayEqual(a[k], b[k])) return false;
  }
  return true;
}
export default function NeedsBasedQuestions({
  setNeedsBasedAnswers,
  needsBasedAnswers,
  hasError
}: {
  setNeedsBasedAnswers: (answers: Answers) => void;
  needsBasedAnswers: Answers | undefined;
  hasError: boolean;
}) {
  const router = useRouter();
  const {
    locale
  } = router;
  const {
    t
  } = useTranslation('needsBasedQuestions');
  const reasonForVisit = useReasonForVisitStore(s => s.reasonForVisit);
  const reasonIdMap = REASON_FOR_VISIT_ID[reasonForVisit];
  const reasonMap = REASONS_FOR_VISIT[reasonIdMap - 1] || null;
  const {
    appointmentFacility
  } = useAppointmentStore(store => store);
  const fc = appointmentFacility?.facilityCode;

  // Keep latest answers in a ref so callbacks don't close over stale state
  const answersRef = useRef<Answers | undefined>(needsBasedAnswers);
  useEffect(() => {
    answersRef.current = needsBasedAnswers;
  }, [needsBasedAnswers]);
  const variables = useMemo(() => {
    return reasonMap?.filterBy?.length && reasonMap.filterBy.length > 0 ? {
      visitReason: reasonMap.filterBy,
      language: locale?.toLowerCase() || 'en-us',
      facilityCode: fc
    } : {
      questionnaireId: 'nbq_public_website',
      language: locale?.toLowerCase() || 'en-us',
      facilityCode: fc
    };
  }, [reasonMap?.filterBy, locale, fc]);
  const [{
    data
  }] = useQuery({
    query: reasonMap?.filterBy?.length && reasonMap.filterBy.length > 0 ? filterQuestionsQuery : needsBasedQuestionsQuery,
    variables
  });
  const questionnaireId: string | undefined = useMemo(() => {
    return data?.questionnaireBy?.questionnaireId || data?.questionnaire?.questionnaireId;
  }, [data]);
  const questions = useMemo(() => {
    return data?.questionnaireBy?.questions || data?.questionnaire?.questions || [];
  }, [data]);
  const onixQuestion = useMemo(() => {
    return questions.find((q: any) => q.questionId === 'onix_interest');
  }, [questions]);
  const setQuestionnaireId = useNeedsBasedQuestionsStore(state => state.setQuestionnaireId);
  const questionnaireInitialAnswersRef = useRef<Answers | undefined>(undefined);
  useEffect(() => {
    if (typeof questionnaireId === 'string') {
      setQuestionnaireId(questionnaireId);
    }
    const current = answersRef.current;
    if (!current) {
      questionnaireInitialAnswersRef.current = undefined;
      return;
    }

    // eslint-disable-next-line no-unused-vars
    const {
      onix_interest,
      ...withoutOnix
    } = current as any;
    questionnaireInitialAnswersRef.current = withoutOnix;
  }, [questionnaireId, setQuestionnaireId]);
  const onixValue = needsBasedAnswers?.onix_interest || [];
  const commitAnswers = useCallback((next: Answers) => {
    const prev = answersRef.current;

    // Dedupe: prevent loops/update storms when Questionnaire re-emits same state
    if (answersEqual(prev, next)) return;
    answersRef.current = next;
    setNeedsBasedAnswers(next);
  }, [setNeedsBasedAnswers]);
  const handleOnixChange = useCallback((values: string[], questionId: string) => {
    const prev = answersRef.current || {};
    const next: Answers = {
      ...prev,
      [questionId]: values
    };
    commitAnswers(next);
  }, [commitAnswers]);
  const handleQuestionnaireUpdate = useCallback((updatedAnswers: Answers) => {
    // If the current questionnaire includes the onix question, preserve or accept its value.
    // Otherwise strip it out so unrelated questionnaires can't re-add/overwrite it.
    const hasOnixQuestion = !!onixQuestion; // safer than checking a string id

    // Use updatedAnswers as the source of truth for all questionnaire answers.
    // Spreading prev would accumulate stale answers from previously-visible
    // conditional questions that are now hidden, causing isNBQFormValid to
    // find empty arrays and block form submission even when all visible
    // questions are answered.
    const next: Answers = {
      ...updatedAnswers
    };

    // The library's isNoneBtnUnselectedByOtherBtn branch (selecting a different
    // answer after "None of the Above") does not initialize child question keys
    // for the newly-selected answer, unlike the regular multi-select path.
    // Without an empty-array entry in needsBasedAnswers, isNBQFormValid has
    // nothing to fail on and the user can continue past an unanswered child question.
    // Detect missing child questions and initialize them here.
    const queue = Object.values(next).flat();
    while (queue.length > 0) {
      const answerId = queue.shift()!;
      if (answerId in next) continue;
      const childQuestion = questions.find((q: QuestionShape) => q.questionId === answerId);
      if (childQuestion) {
        // Both answer types use [] as the unset sentinel so the store type stays string[].
        next[answerId] = [];
      }
    }
    if (hasOnixQuestion) {
      if (updatedAnswers.onix_interest === undefined) {
        next.onix_interest = answersRef.current?.onix_interest || [];
      } else {
        next.onix_interest = updatedAnswers.onix_interest;
      }
    } else {
      delete next.onix_interest;
    }
    commitAnswers(next);
  }, [commitAnswers, onixQuestion, questions]);
  if (!data) return null;
  return <Box mx={2} mt={4} data-sentry-element="Box" data-sentry-component="NeedsBasedQuestions" data-sentry-source-file="NeedsBasedQuestions.tsx">
			<Box mb={2} data-sentry-element="Box" data-sentry-source-file="NeedsBasedQuestions.tsx">
				<Typography variant="header3" data-sentry-element="Typography" data-sentry-source-file="NeedsBasedQuestions.tsx">{t('getToKnow')}</Typography>
			</Box>

			<Box mb={2} data-sentry-element="Box" data-sentry-source-file="NeedsBasedQuestions.tsx">
				<Typography variant="bodyMediumBold" data-sentry-element="Typography" data-sentry-source-file="NeedsBasedQuestions.tsx">{t('provideInfo')}</Typography>
			</Box>

			{onixQuestion && <OnixCard question={onixQuestion} description={onixQuestion.description} questionId={onixQuestion.questionId} value={onixValue} onChangeCallback={handleOnixChange} hasError={hasError} />}

			<Questionnaire questionnaireQuestions={questions.filter((q: any) => q.questionId !== 'onix_interest')} QuestionComponent={QuestionComponent} ScriptingContainer={ScriptingContainer} onAnswersUpdated={handleQuestionnaireUpdate as any} answersInitialState={questionnaireInitialAnswersRef.current} hasError={hasError} data-sentry-element="Questionnaire" data-sentry-source-file="NeedsBasedQuestions.tsx" />
		</Box>;
}